import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, UserPlus, UserCheck, Sparkles, Flame, Crown, Zap, Award } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { REACTIONS, renderCaption, resolveMediaUrl, relativeTime, REPORT_REASONS } from '../lib/social';
import { formatAura } from '../lib/aura';
import UserAvatar from './UserAvatar';
import VideoPlayer from './VideoPlayer';
import CommentSheet from './CommentSheet';

const REACTION_ICONS = { aura: Zap, fire: Flame, respect: Award, king: Crown, legend: Sparkles };

/** Vertical post card for the feed. */
export default function PostCard({ post: initial, onFollow, onDeleted }) {
  const [post, setPost] = useState(initial);
  const [showComments, setShowComments] = useState(false);
  const [reactBurst, setReactBurst] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [reporting, setReporting] = useState(false);
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  useEffect(() => { setPost(initial); }, [initial]);

  const isText = post.type === 'text';
  const isVideo = post.type === 'video' || (post.media?.[0]?.kind === 'video');
  const isPhoto = post.type === 'photo' || post.type === 'aura_moment' || (post.media?.[0]?.kind === 'photo');
  const media = post.media?.[0];

  const currentReaction = post.viewer?.reacted;

  const applyReaction = async (type) => {
    if (!user) { navigate('/auth'); return; }
    setReactBurst(true);
    setTimeout(() => setReactBurst(false), 600);
    try {
      let res;
      if (currentReaction === type) {
        res = await api.delete(`/posts/${post.post_id}/react`);
        setPost((p) => ({
          ...p,
          reactions_total: Math.max(0, (p.reactions_total || 0) - 1),
          reaction_counts: { ...(p.reaction_counts || {}), [type]: Math.max(0, (p.reaction_counts?.[type] || 0) - 1) },
          viewer: { ...p.viewer, reacted: null },
        }));
      } else {
        res = await api.post(`/posts/${post.post_id}/react`, { type });
        setPost((p) => ({
          ...p,
          reactions_total: res.data.reactions_total ?? (p.reactions_total || 0) + (currentReaction ? 0 : 1),
          reaction_counts: res.data.reaction_counts || p.reaction_counts,
          viewer: { ...p.viewer, reacted: type },
        }));
      }
    } catch (_e) { toast.error(t('toast.error')); }
  };

  const doubleTap = (e) => {
    e.preventDefault();
    if (currentReaction !== 'aura') applyReaction('aura');
  };

  const toggleSave = async () => {
    if (!user) { navigate('/auth'); return; }
    try {
      if (post.viewer?.saved) {
        await api.delete(`/posts/${post.post_id}/save`);
        setPost((p) => ({ ...p, viewer: { ...p.viewer, saved: false }, saves_count: Math.max(0, (p.saves_count || 0) - 1) }));
      } else {
        await api.post(`/posts/${post.post_id}/save`);
        setPost((p) => ({ ...p, viewer: { ...p.viewer, saved: true }, saves_count: (p.saves_count || 0) + 1 }));
      }
    } catch (_e) { toast.error(t('toast.error')); }
  };

  const share = async () => {
    const url = `${window.location.origin}/post/${post.public_post_id}`;
    try {
      await api.post(`/posts/${post.post_id}/share`);
      setPost((p) => ({ ...p, shares_count: (p.shares_count || 0) + 1 }));
    } catch (_e) { /* silent */ }
    if (navigator.share) {
      try { await navigator.share({ title: `AURA · @${post.author?.username}`, url }); } catch (_e) { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success(t('feed.linkCopied'));
      } catch (_e) { toast.error(t('toast.error')); }
    }
  };

  const followAuthor = async () => {
    if (!user) { navigate('/auth'); return; }
    try {
      await api.post(`/follow/${post.author.username}`);
      setPost((p) => ({ ...p, viewer: { ...p.viewer, is_following_author: true } }));
      onFollow?.(post.author.username);
      toast.success(t('toast.followed'));
    } catch (_e) { toast.error(t('toast.error')); }
  };

  const deleteMine = async () => {
    if (!window.confirm(t('feed.confirmDelete'))) return;
    try {
      await api.delete(`/posts/${post.post_id}`);
      onDeleted?.(post.post_id);
      toast.success(t('feed.deleted'));
    } catch (_e) { toast.error(t('toast.error')); }
  };

  return (
    <article className="aura-card overflow-hidden" data-testid={`post-${post.public_post_id}`}>
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3">
        <Link to={`/u/${post.author?.username}`} className="flex items-center gap-3 min-w-0 flex-1">
          <UserAvatar user={post.author} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="font-display font-semibold text-white text-sm truncate">@{post.author?.username}</span>
              {post.author?.is_minor && <span className="aura-chip !py-0 !px-1.5 !text-[9px]">TEEN</span>}
            </div>
            <div className="text-[11px] text-aura-textDim flex items-center gap-1.5">
              <Zap size={10} className="text-aura-cyan" />
              <span className="font-mono">{formatAura(post.author?.aura_points || 0)}</span>
              <span className="opacity-40">·</span>
              <span>{relativeTime(post.created_at, locale)}</span>
            </div>
          </div>
        </Link>
        {user && !post.viewer?.is_author && !post.viewer?.is_following_author && (
          <button onClick={followAuthor} data-testid={`follow-${post.author?.username}`} className="aura-btn-ghost !py-1 !px-3 !text-xs">
            <UserPlus size={12} /> {t('profile.follow')}
          </button>
        )}
        {user && post.viewer?.is_following_author && !post.viewer?.is_author && (
          <span className="aura-chip !text-[10px]"><UserCheck size={10} /> {t('profile.following')}</span>
        )}
        <div className="relative">
          <button onClick={() => setShowMenu((s) => !s)} className="text-aura-textDim hover:text-white p-1" data-testid={`menu-${post.public_post_id}`}>
            <MoreHorizontal size={18} />
          </button>
          {showMenu && (
            <div className="absolute right-0 mt-1 w-40 aura-glass rounded-xl shadow-neon-cyan z-10 overflow-hidden">
              {post.viewer?.is_author && (
                <button onClick={deleteMine} className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-white/5" data-testid="post-delete">{t('feed.delete')}</button>
              )}
              {!post.viewer?.is_author && (
                <>
                  <button onClick={() => { setShowMenu(false); setReporting(true); }} className="w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/5" data-testid="post-report">{t('feed.report')}</button>
                  <button onClick={async () => { setShowMenu(false); try { await api.post(`/mutes/${post.author.username}`); toast.success(t('feed.muted')); } catch (_e) { toast.error(t('toast.error')); } }} className="w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/5" data-testid="post-mute">{t('feed.mute')}</button>
                  <button onClick={async () => { setShowMenu(false); try { await api.post(`/blocks/${post.author.username}`); toast.success(t('feed.blocked')); } catch (_e) { toast.error(t('toast.error')); } }} className="w-full text-left px-3 py-2 text-xs text-red-400 hover:bg-white/5" data-testid="post-block">{t('feed.block')}</button>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Body */}
      {isText && (
        <div onDoubleClick={doubleTap} className="px-5 py-6 min-h-[140px] grid place-items-center bg-gradient-to-br from-aura-surface2 to-aura-surface relative overflow-hidden">
          <div className="absolute inset-0 bg-aura-radial opacity-40 pointer-events-none" />
          <p className="relative text-center font-display font-bold text-xl text-white leading-snug break-words">{renderCaption(post.text_body)}</p>
        </div>
      )}
      {isPhoto && media && (
        <div onDoubleClick={doubleTap} className="relative bg-black">
          <img src={resolveMediaUrl(media.url)} alt={post.caption || ''} className="w-full max-h-[70vh] object-contain" loading="lazy" />
        </div>
      )}
      {isVideo && media && <div onDoubleClick={doubleTap}><VideoPlayer post={post} /></div>}
      {reactBurst && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <Zap size={80} className="text-aura-cyan animate-aura-pulse" />
        </div>
      )}

      {/* Category / Aura Moment badge */}
      {post.type === 'aura_moment' && (
        <div className="px-4 pt-3">
          <span className="aura-chip !border-aura-cyan/50 !text-aura-cyan"><Sparkles size={10} /> AURA MOMENT</span>
        </div>
      )}

      {/* Reactions bar */}
      <div className="flex items-center gap-1 px-3 pt-3">
        {REACTIONS.map((r) => {
          const Icon = REACTION_ICONS[r.key];
          const active = currentReaction === r.key;
          return (
            <button
              key={r.key}
              onClick={() => applyReaction(r.key)}
              data-testid={`react-${r.key}-${post.public_post_id}`}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-display font-semibold transition ${active ? 'text-black shadow-neon-cyan' : 'text-white/80 hover:text-white border border-white/10'}`}
              style={active ? { background: r.color } : {}}
              title={r.label}
            >
              <Icon size={12} /> {r.label}
              {(post.reaction_counts?.[r.key] || 0) > 0 && <span className="opacity-70 font-mono">{post.reaction_counts[r.key]}</span>}
            </button>
          );
        })}
      </div>

      {/* Counters / actions */}
      <div className="flex items-center justify-between px-4 py-3 text-xs text-white/70">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1 font-mono"><Zap size={14} className="text-aura-cyan" />{formatAura(post.reactions_total || 0)}<span className="text-aura-textDim ml-0.5">{t('feed.reactionsLabel')}</span></span>
          <button onClick={() => setShowComments(true)} className="inline-flex items-center gap-1 hover:text-white" data-testid={`comments-btn-${post.public_post_id}`}>
            <MessageCircle size={14} /> {formatAura(post.comments_count || 0)}
          </button>
          <button onClick={share} className="inline-flex items-center gap-1 hover:text-white" data-testid={`share-${post.public_post_id}`}>
            <Share2 size={14} /> {formatAura(post.shares_count || 0)}
          </button>
        </div>
        <button onClick={toggleSave} className={`hover:text-white ${post.viewer?.saved ? 'text-aura-yellow' : ''}`} data-testid={`save-${post.public_post_id}`}>
          <Bookmark size={16} fill={post.viewer?.saved ? 'currentColor' : 'none'} />
        </button>
      </div>

      {/* Caption */}
      {(post.caption || (isText && post.text_body)) && !isText && (
        <div className="px-4 pb-3 text-sm text-white/90 break-words">
          <span className="font-display font-semibold text-white mr-1">@{post.author?.username}</span>
          {renderCaption(post.caption)}
        </div>
      )}

      {/* Category label */}
      {post.category && (
        <div className="px-4 pb-3">
          <Link to={`/tag/${post.category}`} className="text-[10px] uppercase tracking-widest text-aura-textDim hover:text-aura-cyan">
            #{post.category}
          </Link>
        </div>
      )}

      {/* Views */}
      {(post.views_count || 0) > 0 && (
        <div className="px-4 pb-3 text-[10px] uppercase tracking-widest text-aura-textDim">
          ▶ {formatAura(post.views_count)} {t('feed.viewsLabel')}
        </div>
      )}

      {showComments && (
        <CommentSheet
          post={post}
          onClose={() => setShowComments(false)}
          onCommentAdded={() => setPost((p) => ({ ...p, comments_count: (p.comments_count || 0) + 1 }))}
        />
      )}

      {reporting && (
        <ReportModal
          onClose={() => setReporting(false)}
          onSubmit={async (reason, description) => {
            try {
              await api.post('/reports', { target_type: 'post', target_id: post.post_id, reason, description });
              toast.success(t('feed.reportSubmitted'));
              setReporting(false);
            } catch (_e) { toast.error(t('toast.error')); }
          }}
        />
      )}
    </article>
  );
}

function ReportModal({ onClose, onSubmit }) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [desc, setDesc] = useState('');
  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/80 backdrop-blur" data-testid="report-modal">
      <div className="w-full max-w-md aura-card p-5">
        <div className="font-display font-bold text-lg mb-4">{t('feed.report')}</div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {REPORT_REASONS.map((r) => (
            <button
              key={r}
              onClick={() => setReason(r)}
              data-testid={`report-reason-${r}`}
              className={`aura-chip !text-[11px] !py-1.5 justify-center ${reason === r ? 'border-aura-cyan text-aura-cyan' : ''}`}
            >
              {t(`feed.reportReasons.${r}`)}
            </button>
          ))}
        </div>
        <textarea
          className="aura-input min-h-[70px] py-2 mb-3"
          placeholder={t('feed.reportDescPlaceholder')}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          maxLength={280}
          data-testid="report-description"
        />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="aura-btn-ghost !py-2 !px-4 !text-sm" data-testid="report-cancel">{t('common.cancel')}</button>
          <button disabled={!reason} onClick={() => onSubmit(reason, desc)} className="aura-btn-primary !py-2 !px-4 !text-sm disabled:opacity-50" data-testid="report-submit">
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
